import {
  WorkflowsRequest,
  WorkflowsTriggersRequest,
  WorkflowsActionsRequest,
  CreateWorkflowRequest,
  GetWorkflowRequest,
  UpdateWorkflowRequest,
  DeleteWorkflowRequest,
  ImportLegacyWorkflowRequest,
} from '../api/requests';
import type { UserStore } from '~/authentication/stores/user';
import type ApiClient from '~/common/services/ApiClient';
import type { RequestPagination } from '~/common/types';
import { Group, GroupID } from '~/groups/types';
import { User } from '~/users/types';
import type {
  Action,
  Step,
  StepRequest,
  Workflow,
  WorkflowRequest,
} from '~/workflows/types';

export class WorkflowService {
  apiClient: ApiClient;
  userStore: UserStore;

  constructor(apiClient: ApiClient, userStore: UserStore) {
    this.apiClient = apiClient;
    this.userStore = userStore;
  }

  async fetch(pagination: RequestPagination, params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new WorkflowsRequest({
      token: accessToken,
      params: { ...pagination, ...params },
    });

    return this.apiClient.request(request);
  }

  async create(workflowRequest: Workflow) {
    const accessToken = this.userStore.accessToken;
    const workflow = this.transform(workflowRequest);

    const request = new CreateWorkflowRequest({ token: accessToken, workflow });

    return this.apiClient.request(request);
  }

  transform(workflow: Workflow): WorkflowRequest {
    const {
      id,
      title,
      description,
      group_id,
      status,
      users,
      groups,
      trigger,
      steps: workflowSteps,
    } = workflow;

    const trigger_ids: string[] = [];
    const user_ids: User['userID'][] = [];
    const group_ids: GroupID[] = [];
    const steps: StepRequest[] = [];

    trigger_ids.push(trigger.id);

    users.forEach((user: User) => {
      user_ids.push(user.userID);
    });

    groups.forEach((group: Group) => {
      group_ids.push(group.groupID as GroupID);
    });

    workflowSteps.data.forEach((step: Step) => {
      const user_ids: User['userID'][] = [];
      const group_ids: GroupID[] = [];
      const action_ids: Action['id'][] = [];
      step.users.data.forEach((user: User) => {
        user_ids.push(user.userID);
      });
      step.groups.data.forEach((group: Group) => {
        group_ids.push(group.groupID as GroupID);
      });

      step.actions.data.forEach((action: Action) => {
        action_ids.push(action.id);
      });

      steps.push({
        action_ids,
        message: step.message,
        user_ids,
        group_ids,
        delay: step.delay,
        active_hours: step.active_hours,
        timezone: step.timezone,
      });
    });

    return {
      id,
      title,
      description,
      group_id,
      status,
      trigger_ids,
      user_ids,
      group_ids,
      steps,
    };
  }

  async get(workflowId: string, params?: { include: string }) {
    const accessToken = this.userStore.accessToken;
    const request = new GetWorkflowRequest({
      token: accessToken,
      workflowId,
      params: { ...params },
    });

    return this.apiClient.request(request);
  }

  async update(workflowId: string, workflowRequest: Workflow) {
    const accessToken = this.userStore.accessToken;
    const workflow = this.transform(workflowRequest);
    const request = new UpdateWorkflowRequest({
      token: accessToken,
      workflowId,
      workflow,
    });

    return this.apiClient.request(request);
  }

  async delete(workflowId: string) {
    const accessToken = this.userStore.accessToken;
    const request = new DeleteWorkflowRequest({
      token: accessToken,
      workflowId,
    });

    return this.apiClient.request(request);
  }

  async updateBase(workflowId: string, workflowRequest: WorkflowRequest) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateWorkflowRequest({
      token: accessToken,
      workflowId,
      workflow: workflowRequest,
    });

    return this.apiClient.request(request);
  }

  async getTriggers(pagination: RequestPagination) {
    const accessToken = this.userStore.accessToken;
    const request = new WorkflowsTriggersRequest({
      token: accessToken,
      params: { ...pagination },
    });

    return this.apiClient.request(request);
  }

  async importFromLegacy(groupId: string) {
    const accessToken = this.userStore.accessToken;
    const request = new ImportLegacyWorkflowRequest({
      token: accessToken,
      groupId,
    });

    return this.apiClient.request(request);
  }

  async getActions(pagination: RequestPagination) {
    const accessToken = this.userStore.accessToken;
    const request = new WorkflowsActionsRequest({
      token: accessToken,
      params: { ...pagination },
    });

    return this.apiClient.request(request);
  }
}
